import * as Schema from "effect/Schema";

import { TrimmedNonEmptyString } from "./baseSchemas";

/**
 * Listing directories on an Environment, so a person can pick a project folder.
 *
 * Adding a project is the one flow that cannot bootstrap itself. Every other
 * screen reads a project that already exists; this one has to name a directory
 * before any project record does. Mobile shipped a complete folder browser
 * against this route and Forge never served it, so someone with no projects had
 * a picker that could not list anything.
 *
 * The shapes here mirror `@t3tools/contracts` exactly, deliberately. That
 * package is the vendored client and does not import this one, so the only
 * thing keeping the two in agreement is that they were written to match and a
 * parity test that fails when they stop matching.
 */

const FILESYSTEM_PATH_MAX_LENGTH = 512;

/**
 * `partialPath` is a directory, not a prefix to complete. The client splits
 * what a person typed into a directory and a filter, sends the directory, and
 * filters the returned names itself. So this never has to guess at intent.
 */
export const FilesystemBrowseInput = Schema.Struct({
  partialPath: TrimmedNonEmptyString.check(Schema.isMaxLength(FILESYSTEM_PATH_MAX_LENGTH)),
  cwd: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(FILESYSTEM_PATH_MAX_LENGTH))),
});
export type FilesystemBrowseInput = typeof FilesystemBrowseInput.Type;

export const FilesystemBrowseEntry = Schema.Struct({
  name: TrimmedNonEmptyString,
  fullPath: TrimmedNonEmptyString,
});
export type FilesystemBrowseEntry = typeof FilesystemBrowseEntry.Type;

/** `parentPath` is the directory that was actually listed, after resolution. */
export const FilesystemBrowseResult = Schema.Struct({
  parentPath: TrimmedNonEmptyString,
  entries: Schema.Array(FilesystemBrowseEntry),
});
export type FilesystemBrowseResult = typeof FilesystemBrowseResult.Type;

export const FilesystemBrowseFailure = Schema.Literals([
  "windows_path_unsupported",
  "current_project_required",
  "read_directory_failed",
]);
export type FilesystemBrowseFailure = typeof FilesystemBrowseFailure.Type;

function decodedFilesystemBrowseErrorMessage(props: object): string | undefined {
  if (!("message" in props)) return undefined;
  return typeof props.message === "string" ? props.message : undefined;
}

/**
 * Carries the request context alongside the classification, because the client
 * renders the message and a person reading "could not browse" with no path in
 * it learns nothing.
 */
export class FilesystemBrowseError extends Schema.TaggedErrorClass<FilesystemBrowseError>()(
  "FilesystemBrowseError",
  {
    partialPath: Schema.optional(TrimmedNonEmptyString),
    cwd: Schema.optional(TrimmedNonEmptyString),
    failure: Schema.optional(FilesystemBrowseFailure),
    parentPath: Schema.optional(TrimmedNonEmptyString),
    platform: Schema.optional(TrimmedNonEmptyString),
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect),
  },
) {
  // Structured diagnostics stay optional for rolling compatibility with legacy
  // message-only payloads, while new call sites must provide the request
  // context and failure classification.
  // @effect-diagnostics-next-line overriddenSchemaConstructor:off
  constructor(props: {
    readonly partialPath: string;
    readonly cwd?: string | undefined;
    readonly failure: FilesystemBrowseFailure;
    readonly parentPath?: string;
    readonly platform?: string;
    readonly cause?: unknown;
  }) {
    const cwd = props.cwd === undefined ? "" : ` from '${props.cwd}'`;
    super({
      ...props,
      message:
        decodedFilesystemBrowseErrorMessage(props) ??
        `Failed to browse filesystem path '${props.partialPath}'${cwd}.`,
    } as any);
  }
}
